// Produkt wyświetlany na listach i sliderach (wariant Wise)

import React, { useState, FC, useMemo, useCallback, useEffect } from 'react';
import classnames from 'classnames';
import { Check2, X } from 'react-bootstrap-icons';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Skeleton } from '@mui/material';

import { useSelector } from 'store';
import {
  usePostProductQuantityDecrement,
  usePostProductQuantityIncrement,
  usePostProductQuantity
} from 'api';
import { IProductListItem, ITechnicalAttribute, IUnit } from 'api/types';
import { AddToCartButton, AddToShoppingListButton, CompareButton, FavoriteButton } from 'components/containers';
import { Counter, Link, Label } from 'components/controls';
import WiseAvailability from 'components/controls/wise/WiseAvailability/WiseAvailability';
import WiseSelect from 'components/wise/WiseSelect';

import styles from 'theme/components/containers/wise/WiseProductItem/WiseProductItem.module.scss';
import productNoImage from 'assets/images/product-no-image.png';

const DESCRIPTION_HEIGHT = 55;

// typ danych wejściowych
interface Props {
  product: IProductListItem;
  categoryId?: number;
  searchKeywords?: string;
  line?: boolean;
  minimalVariant?: boolean;
  imageStretch?: boolean;
  slide?: boolean;
  isSearch?: boolean;
  isPriceLoading?: boolean;
}

const WiseProductItem: FC<Props> = ({
  product,
  line,
  minimalVariant,
  imageStretch,
  slide,
  categoryId,
  searchKeywords,
  isSearch,
  isPriceLoading
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { profile } = useSelector((state) => state.auth);

  // czy rozwinięte są szczegóły
  const [areProductDetails, setAreProductDetails] = useState(false);

  // fallback obrazka
  const [imgError, setImgError] = useState(false);
  const imageUrl = imgError || !product.product_image_formatted_short_info?.images?.[0]?.min
    ? productNoImage
    : product.product_image_formatted_short_info.images[0].min;

  // aktualna ilość
  const [quantity, setQuantity] = useState(1);

  // aktualnie zmieniona ilość (sprzed walidacji)
  const [manuallyChangedQuantity, setManuallyChangedQuantity] = useState<number>();

  // ID aktualnej jednostki
  const [unitId, setUnitId] = useState<number>(
    product.product_unit_short_info?.default_unit_id || 0
  );

  // wysokośc opisu
  const [descriptionHeight, setDescriptionHeight] = useState(0);

  // aktualna jednostka
  const price = useMemo(
    () => product.product_price_short_info?.prices?.find((price) => price.unit_id === unitId),
    [unitId, product]
  );

  // uaktualnienie jednostki
  useEffect(() => {
    if (product.product_unit_short_info?.default_unit_id) {
      setUnitId(product.product_unit_short_info?.default_unit_id);
    }
  }, [product.product_unit_short_info]);

  // zmiana ilości po zmianie jednostki
  useEffect(() => {
    price &&
      setQuantity(
        product.product_unit_short_info?.units?.find((unit) => unitId === unit.unit_id)
          ?.minimal_quantity || 1
      );
  }, [price]);

  useEffect(() => {
    const selector = document.querySelector(`#product-description-${product.id}`);
    const height = selector?.getBoundingClientRect().height;

    if (height) {
      setDescriptionHeight(height);
    }
  }, [product.product_basic_short_info?.description, line]);

  // pobranie informacji o zmniejsonej ilości
  const { mutate: decrementQuantity, isLoading: isDecrementQuantityLoading } =
    usePostProductQuantityDecrement(product.id, {
      onSuccess: (data) => {
        setQuantity(data.data.value);
      }
    });

  // pobranie informacji o zwiększonej ilości
  const { mutate: incrementQuantity, isLoading: isIncrementQuantityLoading } =
    usePostProductQuantityIncrement(product.id, {
      onSuccess: (data) => {
        setQuantity(data.data.value);
      }
    });

  // pobranie informacji ilości
  const {
    mutate: updateQuantity,
    mutateAsync: updateQuantityAsync,
    isLoading: isUpdateQuantityLoading
  } = usePostProductQuantity(product.id, {
    onSuccess: (data) => {
      setQuantity(data.data.value);
    }
  });

  // funkcja renderująca dropdown jednostek
  const renderUnitSelector = useCallback(() => {
    const units = product.product_unit_short_info?.units;
    const unit = units?.find((unit) => unitId === unit.unit_id);
    const hasMultipleUnits =
      !!units && units.length > 1 && profile?.role !== 'ROLE_OPEN_PROFILE';
    return (
      <div
        className={classnames(styles['unit-selector-wrapper'], {
          [styles.unitSelectorWrapperLine]: !!line,
          [styles.unitSelectorWrapperMinimal]: !!minimalVariant
        })}>
        {hasMultipleUnits && units ? (
          <WiseSelect<IUnit>
            onChange={(unit) => unit?.unit_id && setUnitId(unit.unit_id)}
            value={unitId}
            styleGroup="portal"
            options={units.map((unit) => ({
              value: unit.unit_id,
              label: `${unit.name} ${unit.converter !== 1 ? `(${unit.converter})` : ''}`,
              item: unit
            }))}
          />
        ) : (
          <>
            <Trans>Jednostka</Trans>: <strong>{unit?.name}</strong>
          </>
        )}
      </div>
    );
  }, [unitId, product, profile]);

  // funkcja renderująca przycisk dodawania produktu do listy zakupowej
  const renderAddToShoppingListButton = useCallback(
    () => (
      <div
        className={classnames(styles.buttonWrapper, {
          [styles.buttonWrapperLine]: !!line
        })}>
        {profile?.role !== 'ROLE_OPEN_PROFILE' && product.product_shopping_list_short_info && (
          <div className={styles.actionButton}>
            <FavoriteButton
              productId={product.id}
              unitId={product.product_unit_short_info?.default_unit_id || 0}
              inFavourite={product.product_shopping_list_short_info.in_favourite_shopping_list}
            />
          </div>
        )}
        <div className={styles.actionButton}>
          <CompareButton
            productId={product.id}
            title={product.product_basic_short_info?.title}
            image={product.product_image_formatted_short_info?.images?.[0]?.thumb}
          />
        </div>
      </div>
    ),
    [profile, quantity, price, product, line]
  );

  // funkcja renderująca wartość atrybutu
  const renderTechnicalAttributeValue = (item: ITechnicalAttribute) =>
    item.type === 'boolean' ? (
      item.value ? (
        <Check2 className={styles.green} />
      ) : (
        <X className={styles.red} />
      )
    ) : (
      item.value
    );

  const Title = () => {
    return (
      <div
        className={classnames(styles['product-title'], {
          [styles.productTitleLine]: !!line
        })}>
        <Link
          className={styles.title}
          to={`/${product.product_basic_short_info?.url_link}`}
          state={{ categoryId, searchKeywords }}>
          <span itemProp="name">{product.product_basic_short_info?.title}</span>
        </Link>
        <meta
          itemProp="url"
          content={window.location.origin + product.product_basic_short_info?.url_link}
        />
        {line && !!product.product_basic_short_info?.labels?.length && (
          <div className={styles.labelsWrapperLine}>
            {product.product_basic_short_info.labels.map((label) => (
              <Label key={label.type} label={label} />
            ))}
          </div>
        )}
        <span className={styles.text}>
          {t('INDEX')}: {product.product_basic_short_info?.index}
        </span>
        {product.product_basic_short_info?.producer_name && (
          <span className={styles.text} itemScope itemProp="brand" itemType="http://schema.org/Brand">
            {t('Producent')}:{' '}
            <strong itemProp="name">{product.product_basic_short_info?.producer_name}</strong>
          </span>
        )}

        {line && !minimalVariant && product.product_stock_formatted_short_info && (
          <div className={styles.availabilityInTitle}>
            <WiseAvailability stock={product.product_stock_formatted_short_info} />
          </div>
        )}


        {minimalVariant && profile?.role !== 'ROLE_OPEN_PROFILE' && (
          <div
            className={classnames(styles.priceWrapper, {
              [styles.priceWrapperLine]: !!line
            })}>
            {price?.old_price_net_formatted && (
              <div
                className={classnames(styles.old, {
                  [styles.oldLine]: !!line
                })}>
                {price.old_price_net_formatted} {price.currency}
              </div>
            )}
            <div className={styles.net}>
              <strong>
                {price?.price_net_formatted} {price?.currency}
              </strong>{' '}
              {t('netto')}
            </div>
            <div className={styles.gross}>
              {price?.price_gross_formatted} {price?.currency} {t('brutto')}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className={classnames(
        styles.componentWrapper,
        {
          [styles.lineWrapper]: !!line,
          [styles.slide]: !!slide
        },
        'StylePath-Components-Containers-wise-WiseProductItem'
      )}
      itemProp="item"
      itemScope
      itemType="http://schema.org/Product">
      <div
        className={classnames(styles.imageTitleWrapper, {
          [styles.imageStretch]: imageStretch,
          [styles.imageTitleWrapperLine]: !!line
        })}>
        <Link
          to={`/${product.product_basic_short_info?.url_link}`}
          state={{ categoryId, searchKeywords }}
          className={classnames(styles['product-image'], {
            [styles.productImageLine]: !!line,
            [styles.productImageMinimal]: !!minimalVariant
          })}
          style={{
            backgroundImage: `url(${imageUrl})`,
            backgroundSize: 'contain'
          }}>
          <img
            src={product.product_image_formatted_short_info?.images?.[0]?.min || ''}
            alt=""
            style={{ display: 'none' }}
            onError={() => setImgError(true)}
          />
          {!!product.product_basic_short_info?.labels?.length && (
            <div className={styles.labelsWrapper}>
              {product.product_basic_short_info.labels.map((label) => (
                <Label key={label.type} label={label} />
              ))}
            </div>
          )}
        </Link>
        <meta
          itemProp="image"
          content={product.product_image_formatted_short_info?.images?.[0]?.min}
        />
        {!line && !minimalVariant && renderAddToShoppingListButton()}
        {minimalVariant && <Title />}
      </div>

      <div
        className={classnames(styles['product-details'], {
          [styles.productDetailsLine]: !!line,
          [styles.noActions]: (profile?.role as string) === 'ROLE_OPEN_PROFILE'
        })}>
        {!minimalVariant && <Title />}

        <div
          className={classnames(styles['product-actions'], {
            [styles.productActionsLine]: !!line,
            [styles.isSearch]: !!isSearch
          })}>
          {!minimalVariant && !line && (
            <div className={styles['availability-wrapper']}>
              {product.product_stock_formatted_short_info && (
                <WiseAvailability stock={product.product_stock_formatted_short_info} />
              )}
            </div>
          )}

          {line && renderAddToShoppingListButton()}

          <div
            className={classnames(styles.tools, {
              [styles.toolsLine]: !!line
            })}>
            {!minimalVariant && renderUnitSelector()}

            <div
              className={classnames(styles['counter-wrapper'], {
                [styles['counter-wrapper-line']]: line
              })}>
              {profile?.role !== 'ROLE_OPEN_PROFILE' ? (
                <Counter
                  onChange={(value) => updateQuantity({ value, unit_id: unitId })}
                  onDecrease={() => decrementQuantity({ value: quantity, unit_id: unitId })}
                  onIncrease={() => incrementQuantity({ value: quantity, unit_id: unitId })}
                  value={quantity}
                  onChangeValue={(isChanged, newValue) => setManuallyChangedQuantity(newValue)}
                  disabled={
                    isDecrementQuantityLoading ||
                    isIncrementQuantityLoading ||
                    isUpdateQuantityLoading
                  }
                />
              ) : (
                <Link
                  to={`/login?return_url=${encodeURIComponent(
                    location.pathname + location.search
                  )}`}
                  className={styles.loginButton}>
                  <Trans>zaloguj</Trans>
                </Link>
              )}
            </div>
          </div>

          {profile?.role !== 'ROLE_OPEN_PROFILE' && !minimalVariant && (
            <div className={classnames(styles.priceAndCartWrapper, { [styles.priceAndCartWrapperLine]: !!line })}>
              {isPriceLoading ? (
                <div className={styles.priceWrapper}>
                  <Skeleton variant="text" width={100} height={28} />
                  <Skeleton variant="text" width={70} height={18} />
                </div>
              ) : (
              <div
                className={classnames(styles.priceWrapper, {
                  [styles.priceWrapperLine]: !!line
                })}>
                {price?.old_price_net_formatted && (
                  <div
                    className={classnames(styles.old, {
                      [styles.oldLine]: !!line
                    })}>
                    {price.old_price_net_formatted} {price?.currency}
                  </div>
                )}
                <div className={styles.net}>
                  <strong>
                    {price?.price_net_formatted} {price?.currency}
                  </strong>{' '}
                  {t('netto')}
                </div>
                <div className={styles.gross}>
                  {price?.price_gross_formatted} {price?.currency} {t('brutto')}
                </div>
              </div>
              )}

              <AddToCartButton
                isQuantityChanges={!!manuallyChangedQuantity && manuallyChangedQuantity !== quantity}
                updateQuantity={async () => {
                  const newQuantity = manuallyChangedQuantity
                    ? await updateQuantityAsync({
                        value: manuallyChangedQuantity,
                        unit_id: unitId
                      })
                    : undefined;

                  return newQuantity?.data.value === manuallyChangedQuantity
                    ? manuallyChangedQuantity
                    : undefined;
                }}
                quantity={quantity}
                unitId={unitId}
                productId={product.id}
                disabled={
                  (profile?.role as string) === 'ROLE_OPEN_PROFILE' ||
                  isDecrementQuantityLoading ||
                  isIncrementQuantityLoading
                }
              />
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default WiseProductItem;
